import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { apis, useTenantQuery } from 'probonio-shared-ui/module/api';
import { useMeStateSelector } from 'probonio-shared-ui/module/me/types';
import { useCallback } from 'react';

const pdfAvailabilityQueryKey = (contractId: string) => ['benefit', 'bikeleasing', 'contracts', contractId, 'pdf'] as const;

export function useContractPdfAvailable(contractId: string) {
  const tenantId = useMeStateSelector(state => state.me.currentTenantId);
  const queryClient = useQueryClient();

  const { data: pdfAvailable, isPending } = useTenantQuery(
    pdfAvailabilityQueryKey(contractId),
    async tenantId => {
      try {
        await apis.bikeLeasingV2.downloadContractFile({ tenantId, contractId }, { method: 'HEAD' });
        return true;
      } catch (err) {
        if (err instanceof AxiosError && err.response?.status === 404) {
          return false;
        }
        throw err;
      }
    },
    { retry: false },
  );

  const markUnavailable = useCallback(() => {
    if (!tenantId) {
      return;
    }
    queryClient.setQueryData(['tenants', tenantId, ...pdfAvailabilityQueryKey(contractId)], false);
  }, [contractId, queryClient, tenantId]);

  return { pdfAvailable, isCheckingAvailability: isPending, markUnavailable };
}
